/**
 * ÉN ÆNDRINGS-PROCENT FOR HELE APPEN ([D-201], 18-08-2026 — Charlies krav:
 * *"Jeg SKAL bruge YoY, vist som %, både for EBITDA og Resultat før skat. Gør
 * det der skal til, så det kan blive vist og er korrekt."*).
 *
 * BAGGRUNDEN ER EN REGNEFEJL, IKKE ET FORMAT-VALG. Appen havde tre forskellige
 * svar på samme spørgsmål:
 *
 *   1. `marketing-format.deltaPct`  →  (ny − før) / |før|   ✅ rigtigt
 *   2. `BudgetView.deltaRatio`      →  (ny − før) /  før    ❌ vender fortegnet
 *   3. `daily-revenue-digest`       →  nægtede at vise noget når før ≤ 0
 *
 * Nr. 2 er farlig, fordi den ser ud til at virke: går EBITDA fra −1.708 tkr til
 * −1.534 tkr, er det en FORBEDRING på 174 tkr, men (−1534 − (−1708)) / (−1708)
 * = **−10,2 %**. Budgetsiden viste altså et minus for en forbedring — og
 * [D-197] beskriver netop dette tal som "+10 %", så doc og kode var uenige.
 *
 * DEN RIGTIGE NÆVNER ER |før|. Så bevares fortegnet på ÆNDRINGEN:
 *
 *   før      ny       (ny−før)/|før|   læses som
 *   −1.708   −1.534   +10,2 %          underskuddet skrumpede 10,2 % af sidste års
 *   −1.708   −2.000   −17,1 %          underskuddet voksede
 *   −1.000    +500   +150,0 %          fra minus til plus
 *   +1.000    −500   −150,0 %          fra plus til minus
 *
 * ÆRLIGHED ([D-107]) — to ting SKAL følge med tallet ud i UI'et:
 *  - `negativeBase`: var sidste år negativt, er procenten ikke en vækstrate.
 *    Den måler ændringen i forhold til STØRRELSEN af sidste års tal. Uden den
 *    oplysning læses "+10 %" som "vi tjente 10 % mere", hvilket er forkert —
 *    vi tabte 10 % mindre.
 *  - `signFlip`: krydser tallet nul, er procenten teknisk korrekt men næsten
 *    altid mindre interessant end de to absolutte tal. Markeres, ikke skjules.
 *
 * NÅR DER INTET KAN VISES: er sidste år 0 (eller mangler), findes der ingen
 * procent — division med nul. Da returneres `pct: null` MED en årsag, så
 * kaldstedet kan skrive hvorfor i stedet for et tomt felt uden forklaring.
 */

/** Under denne grænse regnes et beløb som nul (kr-afrundinger). */
const EPSILON = 1e-6;

export type YoyMissingReason = 'mangler' | 'nul';

export interface YoyChange {
  /**
   * Ændringen i procent, fortegnsret: **+ betyder bedre, − betyder værre**,
   * også når begge år er negative. null når den ikke kan beregnes.
   */
  pct: number | null;
  /** Ændringen i kroner (ny − før) — altid meningsfuld, også når pct er null. */
  delta: number | null;
  /** Var sammenligningsgrundlaget negativt? Så måles procenten mod |før|. */
  negativeBase: boolean;
  /** Skifter tallet fortegn mellem de to år? */
  signFlip: boolean;
  /** Hvorfor der ingen procent er (kun sat når `pct` er null). */
  reason?: YoyMissingReason;
}

/**
 * Beregner ændringen mellem to perioder. Brug ALTID denne frem for at skrive
 * `(a - b) / b` i et view — det er præcis dén linje der gav budgetsiden et
 * forkert fortegn i et tabsår.
 */
export function yoyChange(value: number | null, prior: number | null): YoyChange {
  if (value === null || prior === null || !Number.isFinite(value) || !Number.isFinite(prior)) {
    return { pct: null, delta: null, negativeBase: false, signFlip: false, reason: 'mangler' };
  }
  const delta = value - prior;
  const negativeBase = prior < -EPSILON;
  const signFlip = Math.abs(value) > EPSILON && Math.abs(prior) > EPSILON && value < 0 !== prior < 0;
  if (Math.abs(prior) < EPSILON) {
    // Fra nul findes ingen procent. Kronerne gør: de returneres alligevel, så
    // kaldstedet kan vise "+412k" frem for et tomt felt uden forklaring.
    return { pct: null, delta, negativeBase: false, signFlip: false, reason: 'nul' };
  }
  const pct = (delta / Math.abs(prior)) * 100;
  return {
    pct: Number.isFinite(pct) ? pct : null,
    delta,
    negativeBase,
    signFlip,
    ...(Number.isFinite(pct) ? {} : { reason: 'mangler' as const }),
  };
}

/**
 * Samme beregning som ratio (ikke procent) til views der selv ganger med 100.
 * Findes for at BudgetView kan skifte til den fælles regel uden at røre sine
 * mange fmtPct-kald.
 */
export function yoyRatio(value: number | null, prior: number | null): number | null {
  const c = yoyChange(value, prior);
  return c.pct === null ? null : c.pct / 100;
}

/**
 * Formaterer procenten dansk: "+11,4 %" / "−3,1 %". Ved |pct| ≥ 100 droppes
 * decimalen — en decimal på 146 % giver ingen information og koster plads.
 *
 * `space` styrer om der er mellemrum før %-tegnet (UI: ja; SMS: nej, hvert
 * tegn tæller mod segment-grænsen).
 */
export function formatYoyPct(pct: number | null, opts: { space?: boolean } = {}): string {
  if (pct === null || !Number.isFinite(pct)) return '';
  const suffix = opts.space === true ? ' %' : '%';
  const abs = Math.abs(pct);
  const num = abs >= 100 ? Math.round(pct).toString() : pct.toFixed(1).replace('.', ',');
  return `${pct >= 0 ? '+' : ''}${num}${suffix}`;
}
